import {
  BookOpen,
  FileSearch,
  FileText,
  Layers3,
  LayoutDashboard,
  LayoutGrid,
  MessagesSquare,
  PenLine,
  PlusSquare,
  Scale,
  UserRoundCog,
  type LucideIcon,
} from "lucide-react";
import { WORKSPACE_GLOBAL_CHAT_LABEL } from "@/apps/rag/constants.js";

export interface AppNavPermission {
  resource: string;
  action: string;
}

export interface AppNavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  permission?: AppNavPermission;
}

export function filterNavByPermission(
  nav: AppNavItem[],
  hasPermission: (resource: string, action: string) => boolean
): AppNavItem[] {
  return nav.filter((item) => {
    if (!item.permission) return true;
    return hasPermission(item.permission.resource, item.permission.action);
  });
}

export interface AppDef {
  id: string;
  name: string;
  description: string;
  basePath: string;
  /** Applications nav / dashboard tile entry; defaults to basePath */
  entryPath?: string;
  icon: LucideIcon;
  available: boolean;
  nav: AppNavItem[];
}

export const APPS_HOME_PATH = "/home";

export const SUITE_TITLE = "Full-stack Legal AI Suite";
export const SUITE_DESCRIPTION =
  "Transform your legal work with AI-powered document analysis. Streamline workflows, enhance compliance, and draft faster with our all-in-one Full-stack Legal AI Suite.";

export const APPS: AppDef[] = [
  {
    id: "chat",
    name: "Chat",
    description:
      "Ask the supervisor to create and manage contract types and custom roles from one conversation.",
    basePath: "/chat",
    icon: MessagesSquare,
    available: true,
    nav: [{ to: "/chat", label: "Chat", icon: MessagesSquare, end: true }],
  },
  {
    id: "contract-review",
    name: "Legal Contract Review",
    description:
      "Upload contracts, run AI risk analysis, and collaborate on review reports.",
    basePath: "/contract-review",
    icon: FileText,
    available: true,
    nav: [
      {
        to: "/contract-review",
        label: "Reviews",
        icon: LayoutDashboard,
        end: true,
        permission: { resource: "review", action: "read" },
      },
      {
        to: "/contract-review/new",
        label: "New Review",
        icon: PlusSquare,
        permission: { resource: "review", action: "create" },
      },
      {
        to: "/contract-review/contract-types",
        label: "Contract Types",
        icon: FileText,
        permission: { resource: "contractType", action: "read" },
      },
      {
        to: "/contract-review/roles",
        label: "Custom Roles",
        icon: UserRoundCog,
        permission: { resource: "customRole", action: "read" },
      },
    ],
  },
  {
    id: "drafting",
    name: "Legal Drafting Workspace",
    description:
      "Draft and refine legal documents with AI-assisted clause suggestions.",
    basePath: "/drafting",
    icon: PenLine,
    available: true,
    nav: [
      {
        to: "/drafting",
        label: "Drafts",
        icon: LayoutDashboard,
        end: true,
        permission: { resource: "draft", action: "read" },
      },
      {
        to: "/drafting/new",
        label: "New Draft",
        icon: PlusSquare,
        permission: { resource: "draft", action: "create" },
      },
      {
        to: "/drafting/templates",
        label: "Templates",
        icon: FileText,
        permission: { resource: "draftTemplate", action: "read" },
      },
      {
        to: "/drafting/legal-roles",
        label: "Legal Roles",
        icon: UserRoundCog,
        permission: { resource: "draftLegalRole", action: "read" },
      },
      {
        to: "/drafting/clause-library",
        label: "Clause Library",
        icon: Layers3,
        permission: { resource: "draftClause", action: "read" },
      },
    ],
  },
  {
    id: "regulatory",
    name: "Regulatory",
    description:
      "Public legal and regulatory knowledge curated by Doculegis administrators.",
    basePath: "/regulatory",
    icon: Scale,
    available: true,
    nav: [
      { to: "/regulatory/chat", label: WORKSPACE_GLOBAL_CHAT_LABEL, icon: MessagesSquare },
      { to: "/regulatory/libraries", label: "Regulatory Libraries", icon: Scale, end: true },
    ],
  },
  {
    id: "rag",
    name: "KM",
    description: "Query your knowledge base and get answers from your documents.",
    basePath: "/rag",
    icon: BookOpen,
    available: true,
    nav: [{ to: "/rag", label: "Knowledge Management", icon: LayoutGrid, end: true }],
  },
  {
    id: "research",
    name: "Legal Research Support",
    description: "Research statutes, case law, and precedents with AI support.",
    basePath: "/research",
    icon: FileSearch,
    available: false,
    nav: [],
  },
];

export function getActiveApp(pathname: string): AppDef | null {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  return (
    APPS.find(
      (app) =>
        normalized === app.basePath ||
        normalized.startsWith(`${app.basePath}/`),
    ) ?? null
  );
}
